<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { availableCaregiversApi } from '@/api/available_caregivers';
import { shiftOfferingsApi, type ShiftOffering } from '@/api/shift-offerings';
import { type Caregiver } from '@/api/caregivers';
import { type PatientFilter } from '@/api/patients';
import { getCareerColor } from '@/utils/careers';
import { endTimeFollowingStart } from '@/utils/time-of-day';
import TimeInput from '@/components/time-input.vue';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ShiftOfferingForm from '../caregivers/shift-offering-form.vue';

const props = defineProps<{
  patient: PatientFilter;
}>();

const { tableProps } = useResponsiveTable();

const emit = defineEmits<{
  close: [];
  success: [];
}>();

const tab = ref('new');

const tomorrow = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
})();

const date = ref('');
const startTime = ref('08:00');
const endTime = ref('20:00');

// The end follows the start, keeping the offered shift's length (see endTimeFollowingStart).
watch(startTime, (newStart, previousStart) => {
  endTime.value = endTimeFollowingStart(previousStart, newStart, endTime.value);
});

const selectedCaregivers = ref<Caregiver[]>([]);
const showOfferingForm = ref(false);
const offeringSent = ref(false);
const search = ref('');

const fieldsSet = computed(() => !!date.value && !!startTime.value && !!endTime.value);

function nextDay(dateStr: string) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

const startDatetime = computed(() => {
  if (!fieldsSet.value) return '';

  return `${date.value}T${startTime.value}`;
});

const endDatetime = computed(() => {
  if (!fieldsSet.value) return '';

  const endDate = endTime.value <= startTime.value ?
    nextDay(date.value) :
    date.value;

  return `${endDate}T${endTime.value}`;
});

const queryParams = computed(() => ({
  patientId: props.patient.id,
  startDatetime: startDatetime.value,
  endDatetime: endDatetime.value,
}));

const { data: availableCaregivers, isPending } = useQuery({
  queryKey: ['availableCaregivers', queryParams],
  queryFn: () => availableCaregiversApi.index(queryParams.value),
  enabled: fieldsSet,
});

const MS_PER_HOUR = 3_600_000;

const DATE_FORMAT_OPTS: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
};

const TIME_FORMAT_OPTS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

function formatDate(d: Date) {
  return d.toLocaleDateString('es-CL', DATE_FORMAT_OPTS);
}

function formatTime(d: Date) {
  return d.toLocaleTimeString('es-CL', TIME_FORMAT_OPTS);
}

const shiftSummary = computed(() => {
  if (!fieldsSet.value) return '';

  const start = new Date(startDatetime.value);
  const end = new Date(endDatetime.value);
  const diffHours = Math.round(
    (end.getTime() - start.getTime()) / MS_PER_HOUR,
  );
  const spansNextDay = endTime.value <= startTime.value;
  const base = `${formatDate(start)} ${formatTime(start)}`;

  if (spansNextDay) {
    return `${base} → ${formatDate(end)} ${formatTime(end)} (${diffHours}h)`;
  }

  return `${base} → ${formatTime(end)} (${diffHours}h)`;
});

const headers = [
  { title: 'Nombre', value: 'firstName', sortable: true },
  { title: 'Apellido', value: 'lastName', sortable: true },
  { title: 'Comuna', value: 'communeName' },
  { title: 'Especialidad', value: 'humanCareer', sortable: true },
];

const queryClient = useQueryClient();

const openOfferingsQueryKey = computed(() => ['shiftOfferings', 'open', props.patient.id]);

const { data: openOfferings } = useQuery({
  queryKey: openOfferingsQueryKey,
  queryFn: () => shiftOfferingsApi.index({ patientId: props.patient.id, aasmState: 'open' }),
});

const closingOfferingId = ref<number | null>(null);
const confirmCloseOffering = ref<ShiftOffering | null>(null);

const { mutate: closeOffering } = useMutation({
  mutationFn: (id: number) => shiftOfferingsApi.update(id, { aasmState: 'closed' }),
  onSuccess: () => {
    closingOfferingId.value = null;
    confirmCloseOffering.value = null;
    queryClient.invalidateQueries({ queryKey: ['shiftOfferings'] });
  },
});

function handleCloseOffering() {
  const offering = confirmCloseOffering.value;
  if (!offering) return;

  closingOfferingId.value = offering.id;
  closeOffering(offering.id);
}

function pendingResponsesCount(offering: ShiftOffering) {
  return offering.shiftOfferingResponses.filter(r => r.aasmState === 'pending').length;
}

function formatExpiration(expiresAt: string) {
  const d = new Date(expiresAt);
  const dateStr = d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  const timeStr = d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: true });

  return `${dateStr} ${timeStr}`;
}

const ALERT_DISMISS_MS = 10_000;

function handleOfferingSuccess() {
  showOfferingForm.value = false;
  offeringSent.value = true;
  selectedCaregivers.value = [];
  queryClient.invalidateQueries({ queryKey: ['shiftOfferings'] });
  setTimeout(() => {
    offeringSent.value = false;
  }, ALERT_DISMISS_MS);
}
</script>

<template>
  <v-card>
    <v-toolbar
      color="white"
      flat
    >
      <v-toolbar-title class="font-weight-bold">
        Ofrecer Turno — {{ patient.fullName }}
      </v-toolbar-title>
      <v-btn
        icon="mdi-close"
        @click="emit('close')"
      />
    </v-toolbar>

    <v-tabs
      v-model="tab"
      color="primary"
      class="rounded-pill text-body-1"
    >
      <v-tab value="active">
        Ofertas activas
        <v-chip
          size="x-small"
          class="ml-2"
        >
          {{ openOfferings?.length ?? 0 }}
        </v-chip>
      </v-tab>
      <v-tab value="new">
        Nueva oferta
      </v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="active">
        <v-card-text>
          <v-table
            v-if="openOfferings?.length"
            density="compact"
          >
            <thead>
              <tr>
                <th class="font-weight-bold">
                  Fecha / Hora
                </th>
                <th class="font-weight-bold">
                  Cupos
                </th>
                <th class="font-weight-bold">
                  Pendientes
                </th>
                <th class="font-weight-bold">
                  Expira
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="offering in openOfferings"
                :key="offering.id"
              >
                <td>{{ offering.humanDate }} {{ offering.humanTime }}</td>
                <td>{{ offering.acceptedCount }} / {{ offering.slots }}</td>
                <td>{{ pendingResponsesCount(offering) }}</td>
                <td>{{ formatExpiration(offering.expiresAt) }}</td>
                <td>
                  <v-btn
                    size="small"
                    variant="outlined"
                    color="error"
                    :loading="closingOfferingId === offering.id"
                    @click="confirmCloseOffering = offering"
                  >
                    Cerrar
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
          <div
            v-else
            class="text-center text-medium-emphasis py-8"
          >
            No hay ofertas activas
          </div>
        </v-card-text>
      </v-window-item>

      <v-window-item value="new">
        <v-card-text class="pt-6">
          <v-row>
            <v-col
              cols="12"
              sm="4"
            >
              <v-text-field
                v-model="date"
                label="Fecha"
                type="date"
                :min="tomorrow"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <time-input
                v-model="startTime"
                label="Hora inicio"
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <time-input
                v-model="endTime"
                label="Hora término"
              />
            </v-col>
          </v-row>

          <v-alert
            v-if="shiftSummary"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ shiftSummary }}
          </v-alert>

          <v-text-field
            v-model="search"
            label="Buscar"
            class="mb-4"
            prepend-inner-icon="mdi-magnify"
            single-line
            variant="underlined"
            hide-details
          />

          <v-data-table
            v-bind="tableProps"
            v-model="selectedCaregivers"
            :headers="headers"
            :header-props="{ class: 'font-weight-bold' }"
            :items="availableCaregivers"
            :search="search"
            :loading="isPending && fieldsSet"
            loading-text="Cargando los profesionales disponibles..."
            no-data-text="Seleccione fecha y hora para ver profesionales disponibles"
            show-select
            return-object
            item-value="id"
            height="300"
            fixed-header
          >
            <template #item.firstName="{ item }">
              {{ item.firstName }}
              <v-icon
                v-if="!item.email"
                color="warning"
                size="small"
                class="ml-1"
              >
                mdi-email-off-outline
              </v-icon>
              <v-tooltip
                v-if="!item.email"
                activator="parent"
                location="bottom"
              >
                Sin email registrado
              </v-tooltip>
            </template>
            <template #item.humanCareer="{ item }">
              <v-chip :color="getCareerColor(item.career)">
                {{ item.humanCareer }}
              </v-chip>
            </template>
            <template #item.data-table-select="{ item, isSelected, toggleSelect }">
              <v-checkbox-btn
                :model-value="isSelected({ value: item, selectable: true })"
                :disabled="!item.email"
                @update:model-value="toggleSelect({ value: item, selectable: true })"
              />
            </template>
          </v-data-table>
        </v-card-text>
      </v-window-item>
    </v-window>

    <v-card-actions
      v-if="tab === 'new'"
      class="justify-end px-6 pb-4"
    >
      <v-btn
        v-if="selectedCaregivers.length > 0"
        color="primary"
        variant="outlined"
        @click="showOfferingForm = true"
      >
        Enviar Oferta ({{ selectedCaregivers.length }})
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="offeringSent && tab === 'new'"
      type="success"
      class="mx-6 mb-4"
    >
      Oferta enviada exitosamente. Los profesionales recibirán un email.
    </v-alert>

    <v-dialog
      v-model="showOfferingForm"
      max-width="500"
    >
      <shift-offering-form
        :selected-caregivers="selectedCaregivers"
        :patient-id="patient.id"
        :start-datetime="startDatetime"
        :end-datetime="endDatetime"
        @close="showOfferingForm = false"
        @success="handleOfferingSuccess"
      />
    </v-dialog>

    <v-dialog
      :model-value="!!confirmCloseOffering"
      max-width="400"
      @update:model-value="confirmCloseOffering = null"
    >
      <v-card>
        <v-card-title>Cerrar Oferta</v-card-title>
        <v-card-text>
          Se cancelarán todas las respuestas pendientes. ¿Deseas continuar?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text="Cancelar"
            @click="confirmCloseOffering = null"
          />
          <v-btn
            color="error"
            text="Cerrar Oferta"
            :loading="closingOfferingId === confirmCloseOffering?.id"
            @click="handleCloseOffering"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
